/* eslint-disable no-console */
import * as Sentry from '@sentry/nextjs'
import { getApp, getApps, initializeApp } from 'firebase/app'
import {
  getToken,
  initializeAppCheck,
  ReCaptchaV3Provider
} from 'firebase/app-check'
import {
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  getAuth,
  getIdToken,
  onAuthStateChanged,
  onIdTokenChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signInWithCustomToken,
  signInWithCredential as firebaseSignInWithCredential,
  linkWithPopup as firebaseLinkWithPopup,
  linkWithCredential as firebaseLinkWithCredential,
  updateEmail,
  User,
  AuthProvider,
  AuthCredential
} from 'firebase/auth'
import {
  getMessaging,
  isSupported as isMessagingSupported
} from 'firebase/messaging'
import { config } from '../../config/index'

// Firebase configuration
const firebaseConfig = {
  appId: config.firebase.auth.appId,
  apiKey: config.firebase.auth.apiKey,
  authDomain: config.firebase.auth.authDomain,
  databaseURL: config.firebase.auth.databaseURL,
  projectId: config.firebase.auth.projectId,
  storageBucket: config.firebase.auth.storageBucket,
  messagingSenderId: config.firebase.auth.messagingSenderId
}

// Initialize Firebase app (ensure it's only initialized once)
const firebaseApp =
  getApps().length === 0 ? initializeApp(firebaseConfig) : getApp()

// Initialize Firebase App Check
const appCheck = (() => {
  try {
    if (typeof window === 'undefined') {
      return null
    }

    // Temporarily exclude AI Studio - testing firebase auth bug
    const isAiStudio = /\.moises\.ai(:\d+)?\/app/.test(window.location.href)
    if (isAiStudio) {
      console.log('AI Studio detected, skipping App Check initialization')
      return null
    }

    if (!config.firebase.recaptchaToken) {
      return null
    }

    return initializeAppCheck(firebaseApp, {
      provider: new ReCaptchaV3Provider(config.firebase.recaptchaToken),
      isTokenAutoRefreshEnabled: true
    })
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Firebase App Check initialization failed:', error)
    return null
  }
})()

// Create a wrapper for App Check to maintain backward compatibility
const FirebaseAppCheckWrapper = {
  getToken: async () => {
    try {
      if (!appCheck) {
        throw new Error('Firebase App Check not initialized')
      }

      // For Firebase v12, use the getToken function from firebase/app-check
      const token = await getToken(appCheck)
      return { token: token.token }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to get App Check token:', error)
      return { token: null }
    }
  }
}

// Create a wrapper object for backward compatibility
export const FirebaseAuth = {
  // Properties
  get currentUser() {
    const user = getAuth(firebaseApp).currentUser
    if (!user) return null

    // Extend the user object with methods that are now standalone functions
    return {
      ...user,
      sendEmailVerification: () => sendEmailVerification(user),
      updateEmail: (newEmail: string) => updateEmail(user, newEmail),
      getIdToken: (forceRefresh?: boolean) => getIdToken(user, forceRefresh)
    }
  },

  // Methods for backward compatibility
  useDeviceLanguage: () => {
    getAuth(firebaseApp).useDeviceLanguage()
  },

  onAuthStateChanged: (callback: (user: User | null) => void) => {
    return onAuthStateChanged(getAuth(firebaseApp), callback)
  },

  onIdTokenChanged: (callback: (user: User | null) => void) => {
    return onIdTokenChanged(getAuth(firebaseApp), callback)
  },

  signInWithEmailAndPassword: (email: string, password: string) => {
    return signInWithEmailAndPassword(getAuth(firebaseApp), email, password)
  },

  createUserWithEmailAndPassword: (email: string, password: string) => {
    return createUserWithEmailAndPassword(getAuth(firebaseApp), email, password)
  },

  signOut: () => {
    console.log('Signing out')
    Sentry.captureMessage('Firebase: Signing out')
    return firebaseSignOut(getAuth(firebaseApp))
  },

  sendPasswordResetEmail: (email: string) => {
    return sendPasswordResetEmail(getAuth(firebaseApp), email)
  },

  signInWithPopup: (provider: any) => {
    return signInWithPopup(getAuth(firebaseApp), provider)
  },

  signInWithRedirect: (provider: any) => {
    return signInWithRedirect(getAuth(firebaseApp), provider)
  },

  signInWithCustomToken: (token: string) => {
    return signInWithCustomToken(getAuth(firebaseApp), token)
  },

  signInWithCredential: (credential: AuthCredential) => {
    return firebaseSignInWithCredential(getAuth(firebaseApp), credential)
  },

  linkWithPopup: (provider: AuthProvider) => {
    const user = getAuth(firebaseApp).currentUser
    if (!user) throw new Error('No authenticated user')
    return firebaseLinkWithPopup(user, provider)
  },

  linkWithCredential: (credential: AuthCredential) => {
    const user = getAuth(firebaseApp).currentUser
    if (!user) throw new Error('No authenticated user')
    return firebaseLinkWithCredential(user, credential)
  }
}

// Set device language for authentication
// eslint-disable-next-line
FirebaseAuth.useDeviceLanguage()

// Initialize Firebase Messaging (only if supported in the browser)
let firebaseMessaging: ReturnType<typeof getMessaging> | null = null

if (typeof window !== 'undefined') {
  isMessagingSupported()
    .then((supported) => {
      if (supported) {
        firebaseMessaging = getMessaging(firebaseApp)
      }
    })
    .catch((err) => {
      console.warn('Firebase Messaging not supported:', err)
    })
}

// eslint-disable-next-line
export const getFirebaseMessaging = () => firebaseMessaging

if (typeof window !== 'undefined') {
  FirebaseAuth.onAuthStateChanged(async (user) => {
    if (user) {
      console.log('Firebase athn state changed: User is signed in')
    } else {
      console.log('Firebase athn state changed: User is signed out')
    }
  })
}

export {
  firebaseApp as FirebaseApp,
  appCheck as FirebaseAppCheck,
  FirebaseAppCheckWrapper
}
